import { Navigate } from "react-router";
import { holdsAny, holdsModule, ROLES } from "../lib/access";
import { DestinationEntrance, ProcessDetail } from "./application-route-helpers";
import type { AppScreen } from "./application-route-types";
import { lazyScreen } from "./lazy-screen";
import { LEAVE_REQUESTS } from "./request-surfaces";

const AccountScreen = lazyScreen(() => import("./account-screen"), "AccountScreen");
const AuditTrail = lazyScreen(() => import("./audit-trail"), "AuditTrail");
const ReportCatalogue = lazyScreen(() => import("./report-catalogue"), "ReportCatalogue");
const CalendarWorkspace = lazyScreen(() => import("./calendar"), "CalendarWorkspace");
const TeamLeaveCalendarWorkspace = lazyScreen(
  () => import("./team-leave-calendar"),
  "TeamLeaveCalendarWorkspace",
);
const Administration = lazyScreen(() => import("./administration"), "Administration");
const Delegations = lazyScreen(() => import("./delegations"), "Delegations");
const Inbox = lazyScreen(() => import("./inbox"), "Inbox");
const Integrations = lazyScreen(() => import("./integrations"), "Integrations");
const PersonDisclosurePolicy = lazyScreen(
  () => import("./person-disclosure-policy"),
  "PersonDisclosurePolicy",
);
const IntegrationStatus = lazyScreen(() => import("./integration-status"), "IntegrationStatus");
const SapIntegration = lazyScreen(() => import("./sap-integration"), "SapIntegration");
const JobArchitecture = lazyScreen(() => import("./job-architecture"), "JobArchitecture");
const Leave = lazyScreen(() => import("./leave"), "Leave");
const LeaveCalendar = lazyScreen(() => import("./leave-calendar"), "LeaveCalendar");
const LeaveCorrections = lazyScreen(() => import("./leave-corrections"), "LeaveCorrections");
const LeaveEntitlementDetail = lazyScreen(
  () => import("./leave-entitlements"),
  "LeaveEntitlementDetail",
);
const LeaveEntitlementHistory = lazyScreen(
  () => import("./leave-entitlements"),
  "LeaveEntitlementHistory",
);
const LeaveEntitlements = lazyScreen(() => import("./leave-entitlements"), "LeaveEntitlements");
const LeavePolicies = lazyScreen(() => import("./leave-policies"), "LeavePolicies");
const LeaveHistory = lazyScreen(() => import("./leave-history"), "LeaveHistory");
const LeaveHistoryDetail = lazyScreen(() => import("./leave-history"), "LeaveHistoryDetail");
const LeaveSetup = lazyScreen(() => import("./leave-setup"), "LeaveSetup");
const CompanyNews = lazyScreen(() => import("./news"), "CompanyNews");
const Messages = lazyScreen(() => import("./messages"), "Messages");
const CompanyPolicies = lazyScreen(() => import("./company-policies/library"), "CompanyPolicies");
const MyDocuments = lazyScreen(() => import("./my-documents"), "MyDocuments");
const ServiceRequests = lazyScreen(() => import("./service-requests"), "ServiceRequests");
const Notifications = lazyScreen(() => import("./notifications"), "Notifications");
const Organisation = lazyScreen(() => import("./organisation"), "Organisation");
const EmployeeWorkspace = lazyScreen(() => import("./employee-workspace"), "EmployeeWorkspace");
const OwnSession = lazyScreen(() => import("./own-session"), "OwnSession");
const People = lazyScreen(() => import("./people"), "People");
const HireCases = lazyScreen(() => import("./hire-workspace"), "HireCases");
const NewHireCase = lazyScreen(() => import("./hire-workspace"), "NewHireCase");
const HireWorkspace = lazyScreen(() => import("./hire-workspace"), "HireWorkspace");
const LeaveRequestsScreen = lazyScreen(() => import("./requests"), "LeaveRequestsScreen");
const RequestsScreen = lazyScreen(() => import("./requests"), "RequestsScreen");
const Setup = lazyScreen(() => import("./setup"), "Setup");
const WorkflowSettings = lazyScreen(() => import("./workflow-settings"), "WorkflowSettings");
const Workplaces = lazyScreen(() => import("./workplaces"), "Workplaces");

export const FOUNDATION_SCREENS: readonly AppScreen[] = [
  { path: "/system/audit-logs", render: ({ state }) => <AuditTrail held={state.riksu} /> },
  {
    path: "/reports/catalog",
    render: ({ state }) => <ReportCatalogue held={state.riksu} />,
  },
  {
    path: "/privacy/field-permissions",
    render: ({ state }) => <PersonDisclosurePolicy roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/announcements",
    render: ({ state }) => <CompanyNews roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/company-policies",
    render: ({ state }) => (
      <CompanyPolicies
        manageHistory={holdsAny(state.riksu.roles ?? [], ROLES.companyPolicies)}
        personId={state.riksu.person?.person_id ?? null}
      />
    ),
  },
  {
    path: "/",
    render: () => <Navigate to="/me/work" replace />,
  },
  {
    path: "/me",
    render: () => <Navigate to="/me/work" replace />,
  },
  {
    path: "/me/work",
    render: ({ state }) => <EmployeeWorkspace session={state.riksu} />,
  },
  {
    path: "/me/profile",
    render: ({ state, photograph }) => <OwnSession state={state} photograph={photograph} />,
  },
  {
    path: "/me/journey",
    render: ({ state, photograph }) => <OwnSession state={state} photograph={photograph} />,
  },
  { path: "/calendar", render: () => <CalendarWorkspace /> },
  {
    path: "/calendar/team-leave",
    render: ({ state }) => (
      <TeamLeaveCalendarWorkspace
        personId={state.riksu.person?.person_id ?? null}
        roles={state.riksu.roles ?? []}
      />
    ),
  },
  // Not scope-gated. An inbox holds what is routed to the caller or to a role they hold,
  // so it is empty rather than forbidden for somebody with nothing waiting -- and hiding
  // it would hide the empty state, which is the answer to "is there anything for me".
  {
    path: "/inbox",
    render: ({ state }) => <DestinationEntrance at="/inbox" state={state} />,
  },
  { path: "/inbox/tasks", render: ({ state }) => <Inbox session={state.riksu} /> },
  {
    path: "/inbox/service-requests",
    render: ({ state }) => <ServiceRequests personId={state.riksu.person?.person_id ?? null} />,
  },
  {
    path: "/inbox/service-requests/:process_id",
    render: ({ state }) => <ProcessDetail state={state} />,
  },
  {
    path: "/inbox/messages/received",
    render: ({ state }) => (
      <Messages personId={state.riksu.person?.person_id ?? null} roles={state.riksu.roles ?? []} />
    ),
  },
  {
    path: "/inbox/messages/received/:thread_id",
    render: ({ state }) => (
      <Messages personId={state.riksu.person?.person_id ?? null} roles={state.riksu.roles ?? []} />
    ),
  },
  {
    path: "/inbox/messages/sent",
    render: ({ state }) => (
      <Messages personId={state.riksu.person?.person_id ?? null} roles={state.riksu.roles ?? []} />
    ),
  },
  {
    path: "/inbox/messages/sent/:thread_id",
    render: ({ state }) => (
      <Messages personId={state.riksu.person?.person_id ?? null} roles={state.riksu.roles ?? []} />
    ),
  },
  {
    path: "/inbox/notices",
    render: ({ state }) => <Notifications roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/inbox/notices/:notification_id",
    render: ({ state }) => <Notifications roles={state.riksu.roles ?? []} />,
  },
  // THE LOGIN, WHICH IS NOT THE PERSON. It shared `/` with the reader's own record until
  // the product owner asked why — see `account-screen.tsx` and the note on the destination.
  // No roles and no principal: it renders the session document it is handed and nothing
  // else, and it is the one screen that is useful to a login riksu resolves to nobody.
  { path: "/account", render: ({ state }) => <AccountScreen state={state} /> },
  {
    path: "/documents/mine",
    render: ({ state }) => (
      <MyDocuments
        person={state.riksu.person}
        payrollEnabled={holdsModule(state.riksu, "payroll")}
      />
    ),
  },
  {
    path: "/account/delegations",
    render: ({ state }) => <Delegations personId={state.riksu.person?.person_id ?? null} />,
  },
  // THE AREA'S OWN SCREEN, and it takes NOTHING. It used to be handed `state.riksu` so its
  // list of areas could be narrowed by the same `Held` the rail is narrowed with; the
  // product owner has removed that list — *"it is already on the left"* — and nothing left
  // on the screen varies by who is asking, so the prop went with the narrowing it fed. See
  // `administration.tsx`.
  { path: "/administration", render: () => <Administration /> },
  // THE ROLES GO IN for the same reason they do on `/setup/:step` below: the chart offers
  // "put somebody in this seat", and `assign_to_position` takes `organization.admin` or
  // `people.hr`. Offering it to everybody would be a `+` on every row that answers 403.
  {
    path: "/organisation",
    render: ({ state }) => <Organisation roles={state.riksu.roles ?? []} />,
  },
  // LEVEL TWO, which the chart beside it does not draw — and since 2026-08-07 the ONE
  // screen that draws it at all.
  //
  // THE ROLES GO IN, which they did not while this screen only read. Both of its reads
  // are `Subject::Impersonal` and take `riksu.read` alone, so nothing about the LIST is
  // narrowed and none of it is hidden; what the roles decide is whether "Register a
  // workplace" is offered, because `create_workplace` takes `STRUCTURE_ADMINISTRATORS`
  // and a button that always answers 403 teaches somebody the product is broken. Same
  // reasoning, same line, as `/organisation` directly above.
  {
    path: "/setup/workplaces",
    render: ({ state }) => <Workplaces roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/setup/job-architecture",
    render: ({ state }) => <JobArchitecture roles={state.riksu.roles ?? []} />,
  },
  // ONE ROUTE PER STEP, and a redirect from the bare path. `/setup/positions` is a
  // PLACE: it is what a bookmark keeps, what a person reads in the address bar, what a
  // server could route and what an analytics tool counts as a page. This shipped for an
  // hour as `?step=jobs`, which is a parameter pretending to be a place — a parameter
  // NARROWS a place, it does not get to be one.
  //
  // `replace` on the redirect, so Back from the first step leaves the module rather than
  // bouncing off `/setup` and landing where it started.
  { path: "/setup", render: () => <Navigate to="/setup/legal-entities" replace /> },
  // THE ROLES GO IN, because the screen refuses politely rather than rendering six forms
  // every one of whose writes riksu will refuse. The server decides regardless; this
  // decides what is worth offering.
  {
    path: "/setup/:step",
    render: ({ state }) => <Setup roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/workflow",
    render: ({ state }) => <WorkflowSettings roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/integrations",
    render: ({ state }) => <Integrations issuer={state.riksu.issuer} />,
  },
  {
    path: "/integrations/sap",
    render: () => <SapIntegration />,
  },
  {
    path: "/integrations/status",
    render: ({ state }) => <IntegrationStatus roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/integrations/:connection_id",
    render: ({ state }) => <Integrations issuer={state.riksu.issuer} />,
  },
  // ADMINISTRATION, AT AN ADDRESS UNDER `/leave` — which is a destination in its own right
  // and not a section of the leave module. `9110d4d` moved it and deliberately left the
  // address where it was, because `moduleFor` resolves by LONGEST prefix and `/leave/setup`
  // beats `/leave` for its own address.
  //
  // This comment read "a sibling surface of the leave module, reached from the module
  // sidebar rather than from a rail slot of its own" until the move made every clause of it
  // false, and it is the only place in the router that says which module this belongs to.
  {
    path: "/leave/setup",
    render: ({ state }) => <LeaveSetup roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/leave/entitlements",
    render: ({ state }) => <LeaveEntitlements roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/leave/entitlements/policies",
    render: ({ state }) => <LeavePolicies roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/leave/entitlements/history",
    render: ({ state }) => <LeaveEntitlementHistory roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/leave/entitlements/history/:entitlement_id",
    render: ({ state }) => <LeaveEntitlementDetail roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/people",
    render: ({ administering, state }) => (
      <People
        administering={administering}
        roles={state.riksu.roles ?? []}
        performanceLicensed={holdsModule(state.riksu, "performance")}
        examiningSuccession={
          holdsAny(state.riksu.roles, ROLES.workforce) && holdsModule(state.riksu, "performance")
        }
        examiningCertificates={
          holdsAny(state.riksu.roles, ROLES.professionalCertificates) &&
          holdsModule(state.riksu, "people")
        }
        mayBroadcast={
          holdsAny(state.riksu.roles, ROLES.messagingBroadcast) &&
          holdsModule(state.riksu, "messaging")
        }
        observingNotices={
          holdsAny(state.riksu.roles, ROLES.offboarding) && holdsModule(state.riksu, "offboarding")
        }
        observingJoinings={
          holdsAny(state.riksu.roles, ROLES.onboarding) && holdsModule(state.riksu, "onboarding")
        }
        observingAbsences={
          holdsAny(state.riksu.roles, ROLES.leave) && holdsModule(state.riksu, "leave")
        }
        origin={state.riksu}
      />
    ),
  },
  { path: "/directory", render: () => <Navigate to="/people?view=directory" replace /> },
  { path: "/people/hires", render: () => <HireCases /> },
  { path: "/people/hires/new/:source?", render: () => <NewHireCase /> },
  { path: "/people/hires/:hire_case_id", render: () => <HireWorkspace /> },
  { path: "/people/hires/:hire_case_id/:step", render: () => <HireWorkspace /> },
  // No `administering`: leave is the caller's OWN, and `riksu-authz` filters the balances
  // to what they may see. A screen that widened on a scope would be deciding "about whom"
  // from "may you call this".
  // THE PRINCIPAL GOES IN, which is the root cause of a defect worth naming here rather
  // than only in the screen. `/v1/leave/balances` returns the caller's own for an
  // ordinary employee and EVERYBODY'S for `people.hr` or `leave.admin` — so a screen
  // handed no principal cannot tell its own balances from a stranger's, and the request
  // pane defaulted to `balances[0]`. `person_id` is a UUIDv7, so that is the
  // earliest-created person in the tenant: the same stranger every time.
  //
  // `/expense` four lines below already did this. The two are now the same shape.
  {
    path: "/leave",
    render: ({ state }) => <DestinationEntrance at="/leave" state={state} />,
  },
  {
    path: "/leave/balances",
    render: ({ state }) => <Leave personId={state.riksu.person?.person_id ?? null} />,
  },
  // NO `roles` AND NO `personId`, which is the whole of the screen's audience decision.
  // `GET /v1/leave/sickness-overlaps` is `Subject::SpecialCategoryList` narrowed with
  // `themself_only`, so an ordinary employee is handed their own corrections and a health
  // administrator the company's — and riksu is what decides which. A `roles` gate here
  // would hide the empty state from the worker whose holiday the rapor ran through, and the
  // empty state is the answer to *am I owed days back*.
  {
    path: "/leave/corrections",
    render: () => <LeaveCorrections />,
  },
  {
    path: "/leave/calendar",
    render: ({ state }) => (
      <LeaveCalendar
        personId={state.riksu.person?.person_id ?? null}
        roles={state.riksu.roles ?? []}
      />
    ),
  },
  {
    path: "/leave/history",
    render: ({ state }) => <LeaveHistory personId={state.riksu.person?.person_id ?? null} />,
  },
  {
    path: "/leave/history/:absence_id",
    render: ({ state }) => (
      <LeaveHistoryDetail
        personId={state.riksu.person?.person_id ?? null}
        roles={state.riksu.roles ?? []}
      />
    ),
  },
  // WHAT I SENT, AND WHAT IS WAITING ON ME — two addresses where there was one segmented
  // control on the balances screen. The `showing` a query parameter used to carry is the
  // ROUTE's answer now; `requests.tsx`'s header carries the argument, and it is
  // `destinations.ts`'s own: a parameter narrows a place, it does not get to be one.
  //
  // THE PRINCIPAL GOES IN for `/leave`'s reason exactly — it decides whether the *whose*
  // column is drawn, and never what either list holds, which riksu narrowed before this
  // screen saw it.
  {
    path: "/leave/requests",
    render: ({ state }) => <LeaveRequestsScreen personId={state.riksu.person?.person_id ?? null} />,
  },
  {
    path: "/leave/requests/:process_id",
    render: ({ state }) => <ProcessDetail state={state} />,
  },
  {
    path: "/leave/approvals",
    render: ({ state }) => (
      <RequestsScreen
        surface={LEAVE_REQUESTS}
        showing="awaiting"
        personId={state.riksu.person?.person_id ?? null}
      />
    ),
  },
  {
    path: "/leave/approvals/:process_id",
    render: ({ state }) => <ProcessDetail state={state} />,
  },
];
